/**
 * Il sorteggio dei servizi nei riquadri che li promuovono (19/10/2026).
 *
 * La regola, chiesta dal committente sul modello di «Strumenti per
 * decidere» in prima pagina: un riquadro che ha posto per N servizi mostra
 * N servizi diversi, pescati a caso fra i quattordici del catalogo, e a
 * ogni caricamento ne escono altri. Niente animazioni e niente caroselli a
 * tempo: cambia la pagina, non il riquadro mentre lo si guarda.
 *
 * Due vincoli valgono per tutti i chiamanti:
 *
 *   - sulla pagina di un servizio quel servizio non esce mai (`escludi`);
 *   - nella stessa pagina un servizio non esce due volte. Dove i riquadri
 *     sono più d'uno, chi rende la pagina sorteggia **una volta sola** e
 *     distribuisce: il secondo sorteggio esclude quello che è uscito nel
 *     primo.
 *
 * L'insieme è `AREE_PAGINA` di lib/serviziPagina.ts, la stessa fonte di
 * «Strumenti» e delle card del catalogo (`CardServizio`), quindi numeri,
 * titoli, frasi e disegni sono quelli già pubblicati.
 *
 * Sta in `lib/` e senza direttiva perché lo leggono sia i Server Component
 * delle rotte dinamiche (sorteggio a ogni richiesta, l'HTML arriva già con
 * la scelta) sia i Client Component delle rotte statiche (sorteggio dopo
 * l'idratazione): una funzione esportata da un file "use client" e letta
 * dal server non è la funzione, è il riferimento al modulo client (vedi
 * LEAD_DISPACCI nei learnings).
 */

import { AREE_PAGINA, type AreaPagina, type ServizioPagina } from "@/lib/serviziPagina";

/** I quattordici servizi, nell'ordine del catalogo. */
export const SERVIZI_CATALOGO: ServizioPagina[] = AREE_PAGINA.flatMap((a) => a.servizi);

/** Il servizio di uno slug, `undefined` se lo slug non è del catalogo. */
export function servizioDi(slug: string): ServizioPagina | undefined {
  return SERVIZI_CATALOGO.find((s) => s.slug === slug);
}

/** L'area («domanda» di /servizi) a cui appartiene un servizio. */
export function areaDi(slug: string): AreaPagina | undefined {
  return AREE_PAGINA.find((a) => a.servizi.some((s) => s.slug === slug));
}

/** Mescola una copia (Fisher-Yates): ogni ordine ha la stessa probabilità,
 *  al contrario del `sort(() => Math.random() - 0.5)`, che è sbilanciato. */
function mescola<T>(voci: readonly T[]): T[] {
  const copia = [...voci];
  for (let i = copia.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copia[i], copia[j]] = [copia[j]!, copia[i]!];
  }
  return copia;
}

/** N slug distinti, a caso, fra i servizi del catalogo meno quelli in
 *  `escludi`. Se ne restano meno di N li restituisce tutti. */
export function sorteggiaServizi(n: number, { escludi = [] }: { escludi?: readonly string[] } = {}): string[] {
  const fuori = new Set(escludi);
  return mescola(SERVIZI_CATALOGO.filter((s) => !fuori.has(s.slug)))
    .slice(0, Math.max(0, n))
    .map((s) => s.slug);
}

/** Un servizio a caso per ogni area, nell'ordine delle aree: è il
 *  sorteggio di «Strumenti per decidere», che deve restare vero a
 *  «Quattro domande, quattro servizi». Un'area senza servizi
 *  sorteggiabili (tutti in `escludi`) salta. */
export function sorteggiaPerArea({ escludi = [] }: { escludi?: readonly string[] } = {}): {
  area: AreaPagina;
  servizio: ServizioPagina;
}[] {
  const fuori = new Set(escludi);
  return AREE_PAGINA.flatMap((area) => {
    const candidati = area.servizi.filter((s) => !fuori.has(s.slug));
    if (candidati.length === 0) return [];
    return [{ area, servizio: candidati[Math.floor(Math.random() * candidati.length)]! }];
  });
}

/** Le tre card di chiusura delle pagine dei servizi e di /contatti
 *  (`AltriServizi`) **prima dell'idratazione**: sono rotte statiche, quindi
 *  il sorteggio lo fa il browser dopo, e l'HTML del build ha bisogno di
 *  una scelta fissa che server e prima resa del client condividano. È la
 *  scelta che le pagine facevano a mano fino al 19/10/2026 (le tre card
 *  dei modelli; per monitoraggio, report, verifica e analisi gli altri
 *  servizi della stessa area). La vede chi non ha JavaScript e chi indicizza. */
export const PRIMA_SCELTA_ALTRI: Record<string, string[]> = {
  monitoraggio: ["report", "verifica-informativa", "analisi"],
  report: ["monitoraggio", "verifica-informativa", "analisi"],
  "verifica-informativa": ["monitoraggio", "report", "analisi"],
  analisi: ["monitoraggio", "report", "verifica-informativa"],
  "presenza-internazionale": ["import-export", "materie-prime", "due-diligence"],
  "import-export": ["presenza-internazionale", "materie-prime", "due-diligence"],
  "materie-prime": ["presenza-internazionale", "import-export", "sanzioni"],
  "due-diligence": ["verifica-informativa", "rischio-reputazionale", "sanzioni"],
  "rischio-reputazionale": ["verifica-informativa", "due-diligence", "sanzioni"],
  sanzioni: ["due-diligence", "rischio-reputazionale", "import-export"],
  "cibernetica-aziendale": ["informatica", "pianificazione-strategica", "giuridico"],
  informatica: ["cibernetica-aziendale", "pianificazione-strategica", "giuridico"],
  giuridico: ["due-diligence", "sanzioni", "cibernetica-aziendale"],
  "pianificazione-strategica": ["cibernetica-aziendale", "analisi", "report"],
  /** /contatti, che non è la pagina di un servizio. */
  contatti: ["report", "verifica-informativa", "analisi"],
};

/** Un intero a caso fra `min` e `max` compresi: quanti servizi mostrare
 *  dove anche il numero ruota (il riquadro grande dei riservati, da uno a
 *  tre). Sta qui, e non nel render della pagina, perché `Math.random` in
 *  un componente è un errore di lint (`react-hooks/purity`). */
export function sorteggiaQuanti(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}
